import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import React, { useState } from 'react';
import { Icon } from 'ui';
import AccordionHeaderToggle, { AccordionItemContentIdContext, getInnerToggleSummaryProps } from './AccordionHeaderToggle';

export type AccordionList2Tab = {
  assignment?: { guid?: string };
  children?: React.ReactNode;
  className?: string;
  defaultExpanded?: boolean;
  disabled?: boolean;
  expanded?: boolean;
  hasInnerToggle?: boolean;
  id?: string;
  isHeaderDecorative?: boolean;
  onClick?: React.MouseEventHandler<HTMLElement>;
  title?: React.ReactNode;
};

type AccordionList2ItemProps = Readonly<{
  contentId: string;
  headerId: string;
  index: number;
  item: AccordionList2Tab;
  onChange: (item: AccordionList2Tab, expanded: boolean) => void;
  onEntered: (index: number) => void;
  setAccordionRef: (index: number, element: HTMLElement | null) => void;
  shouldScrollIntoViewOnExpand: boolean;
}>;

function getSummaryProps(item: AccordionList2Tab, headerId: string): Record<string, unknown> {
  if (item.hasInnerToggle) {
    return { id: headerId, ...getInnerToggleSummaryProps() };
  }
  if (item.isHeaderDecorative) {
    return { 'aria-hidden': true, className: 'accordion-list2__summary--decorative', id: headerId, tabIndex: -1 };
  }
  return { id: headerId };
}

function AccordionList2Item({
  contentId,
  headerId,
  index,
  item,
  onChange,
  onEntered,
  setAccordionRef,
  shouldScrollIntoViewOnExpand,
}: AccordionList2ItemProps): JSX.Element {
  // El conmutador no puede leer el contexto interno de MUI (el bundler resuelve la build ESM y el import directo la CJS);
  // su estado vive aquí para nacer y morir con el panel de MUI, que solo lee `defaultExpanded` al montar
  const [isInnerExpanded, setIsInnerExpanded] = useState<boolean>(() => Boolean(item.defaultExpanded));

  function handleChange(_event: unknown, expanded: boolean): void {
    setIsInnerExpanded(expanded);
    onChange(item, expanded);
  }

  function handleEntered(): void {
    onEntered(index);
  }

  function handleRef(element: HTMLElement | null): void {
    setAccordionRef(index, element);
  }

  return (
    <Accordion
      ref={handleRef}
      className={item.className}
      defaultExpanded={item.defaultExpanded}
      disabled={item.disabled}
      expanded={item.expanded}
      id={item.id}
      onChange={handleChange}
      onClick={item.onClick}
      TransitionProps={shouldScrollIntoViewOnExpand ? { onEntered: handleEntered } : undefined}
    >
      <AccordionSummary
        expandIcon={item.isHeaderDecorative ? undefined : <Icon name="chevron-down" size="xl" />}
        aria-controls={contentId}
        {...getSummaryProps(item, headerId)}
      >
        {item.hasInnerToggle && (
          <AccordionHeaderToggle contentId={contentId} isDisabled={Boolean(item.disabled)} isExpanded={item.expanded ?? isInnerExpanded} />
        )}
        <AccordionItemContentIdContext.Provider value={contentId}>{item.title}</AccordionItemContentIdContext.Provider>
      </AccordionSummary>
      <AccordionDetails>{item.children}</AccordionDetails>
    </Accordion>
  );
}

export default AccordionList2Item;
